<template>
  <div class="view--presale-detail">
    <b-row class="mb-4" v-if="!loading">
      <b-col md="12" lg="6">
        <b-card>
          <b-card-title>Resultaat</b-card-title>

          <template v-if="presale === null">
            <p>De gegevens van deze presale ontbreken.</p>
          </template>

          <template v-else>
            <b-table-simple small>
              <b-tr v-if="presale.product_identifier !== null">
                <b-th>
                  Productidentifier
                </b-th>
                <b-td>
                    {{ presale.product_identifier }}
                </b-td>
              </b-tr>

              <b-tr v-if="presale.additional_identifier !== null">
                <b-th>
                  Extra identifier
                </b-th>
                <b-td>
                    {{ presale.additional_identifier }}
                </b-td>
              </b-tr>

              <b-tr>
                <b-th>
                  Virtuele stock
                </b-th>
                <b-td>
                  <input v-if="virtualStockIsEditable"
                         v-model="presale.virtual_stock"
                         type="number"
                         id="virtual_stock"
                         required="required"
                         class="form-control"
                  />
                  <span v-else>
                    {{ presale.virtual_stock }}
                  </span>
                </b-td>
              </b-tr>

              <b-tr>
                <b-th>
                  Presale begint op
                </b-th>
                <b-td>
                  <input v-if="presaleStartDateIsEditable"
                         v-model="presale.presale_starts_at"
                         type="date"
                         id="presale_starts_at"
                         required="required"
                         class="form-control"
                  />
                  <span v-else>
                    {{ parseDate(presale.presale_starts_at) }}
                  </span>
                </b-td>
              </b-tr>

              <b-tr>
                <b-th>
                    Presale loopt tot
                </b-th>
                <b-td>
                  <input v-if="presaleEndDateIsEditable"
                         v-model="presale.presale_ends_at"
                         type="date"
                         id="presale_ends_at"
                         required="required"
                         class="form-control"
                  />
                  <span v-else>
                    {{ parseDate(presale.presale_ends_at) }}
                  </span>
                </b-td>
              </b-tr>

              <b-tr v-if="presale.release_date !== null">
                <b-th>
                  Release datum
                </b-th>
                <b-td>
                    <input v-if="presaleReleaseDateIsEditable"
                           v-model="presale.release_date"
                           type="date"
                           id="release_date"
                           required="required"
                           class="form-control"
                    />
                    <span v-else>
                      {{ parseDate(presale.release_date) }}
                    </span>
                </b-td>
              </b-tr>

              <b-tr>
                <b-th>
                  Aantal voorverkocht
                </b-th>
                <b-td>
                    {{ presale.sold_quantity }}
                </b-td>
              </b-tr>

              <b-tr>
                <b-th>
                  Aantal geannuleerd
                </b-th>
                <b-td>
                    {{ presale.cancelled_quantity }}
                </b-td>
              </b-tr>
            </b-table-simple>

            <div class="action-buttons">
              <b-button @click="cancelButtonHandler">
                <template v-if="thereAreChanges && isEditable"> Annuleren </template>
                <template v-else> Terug naar overzicht </template>
              </b-button>
              <b-button
                @click="saveButtonHandler"
                :disabled="!thereAreChanges"
                :class="{ 'btn-success': thereAreChanges }"
                v-if="thereAreChanges"
              >
                Opslaan
              </b-button>
            </div>
          </template>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<style lang="scss">
.view--presale-detail {
  .action-buttons {
    display: flex;
    gap: 0.5rem;
    button {
      &:disabled {
        cursor: not-allowed;
      }
    }
  }
}
</style>

<script>
import { cloneDeep, isEqual } from "lodash";
import { date } from "@/helpers/date.js"

export default {
  name: "PresaleDetail",

  props: {
    presaleId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: true,
      presale: null,
      originalPresale: {},
      presaleStartDateIsEditable: true,
    };
  },

  computed: {
    thereAreChanges() {
      return !isEqual(this.originalPresale, this.presale);
    },

    isEditable() {
      return !this.presaleHasFinished;
    },

    presaleHasFinished() {
      return this.presale.release_date !== null && new Date(this.presale.release_date) <= new Date();
    },

    presaleIsRunning() {
      return this.presale.presale_starts_at !== null && new Date(this.presale.presale_starts_at) <= new Date() && new Date(this.presale.presale_ends_at) > new Date();
    },

    presaleEndDateIsEditable() {
      if (this.presaleHasFinished) {
        return false;
      }

      return true;
    },

    presaleReleaseDateIsEditable() {
      return false; // always false for now.
    },

    virtualStockIsEditable() {
      if (this.presaleHasFinished) {
        return false;
      }

      return (this.presale.release_date === null || new Date(this.presale.release_date) > new Date());
    },
  },

  methods: {
    parseDate(val) {
      return val ? date(val) : null  // Prevents showing 1970-01-01
    },

    cancelButtonHandler() {
      this.$router.go(-1);
    },

    saveButtonHandler() {
        const presale = this.presale;

        this.$http({
            method: 'PATCH',
            url: `/api/rest/presale-schedules/${this.presaleId}`,
            data: presale
        })
        .catch(error => {
            this.$alert(`Fout tijdens opslaan van gegevens: ${error}`, 'error');
        })
        .then(() => {
            this.$alert(`Presale succesvol opgeslagen.`);
            this.originalPresale = cloneDeep(this.presale);

            this.presaleStartDateIsEditable = this.isPresaleStartDateEditable();
        })
    },

    isPresaleStartDateEditable() {
      // Checks if the presale start date should be editable and
      // puts the result in this.presaleStartDateEditable, so the GUI
      // can show either an <input> or a <span> element.
      const today    = new Date();
      const presaleStartsAt = this.presale.presale_starts_at;

      if (this.presaleIsRunning || this.presaleHasFinished) {
        return false;
      }

      if (presaleStartsAt === null || new Date(presaleStartsAt) > today) {
        return true;
      }
      
      return false;
    },
  },

  mounted() {
    this.$http({
      method: 'GET',
      url: `/api/rest/presale-schedules/${this.presaleId}`,
      params: {
        include: 'sold_quantity,cancelled_quantity'
      }
    })
        .then((response) => {
          const data = response.data;
          const presale_starts_at = data.presale_starts_at ? data.presale_starts_at.split(' ')[0] : null;
          const presale_ends_at = data.presale_ends_at ? data.presale_ends_at.split(' ')[0] : null;

          this.originalPresale = Object.assign({}, data, { presale_starts_at, presale_ends_at });
          this.presale = cloneDeep(this.originalPresale);

          this.presaleStartDateIsEditable = this.isPresaleStartDateEditable();
        })
        .catch((error) => {
          this.$alert(`Fout tijdens ophalen van presale met ID ${this.presaleId}: ${error}`, 'error')
        })
        .then(() => { this.loading = false; })
  },
};
</script>